package dairy.mynotes.navigation

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.navigation.NavHostController
import androidx.navigation.NavType
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.rememberNavController
import androidx.navigation.navArgument
import dairy.mynotes.addnotes.AddNotesScreen
import dairy.mynotes.login.LoginScreen
import dairy.mynotes.noteslist.NotesListScreen
import dairy.mynotes.registration.RegistrationScreen
import dairy.mynotes.updatenotes.UpdateNotesScreen
import dairy.mynotes.firebase.FirebaseService
import kotlinx.coroutines.flow.first

/**
 * Main navigation graph for the app
 * Sets up all routes and their corresponding screens
 */
@Composable
fun AppNavigation(
    navController: NavHostController = rememberNavController()
) {
    // null = still checking, true/false = resolved
    var startDestination by remember { mutableStateOf<String?>(null) }

    // Observe Firebase auth state — on JS the first emission comes asynchronously
    // after the SDK restores the persisted session from IndexedDB.
    LaunchedEffect(Unit) {
        startDestination = try {
            val user = FirebaseService.auth.authStateChanged.first()
            if (user != null) Screen.NotesList.route else Screen.Login.route
        } catch (e: Exception) {
            Screen.Login.route
        }
    }

    // Show a centered spinner while we wait for Firebase to restore session
    val resolvedDestination = startDestination ?: run {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator()
        }
        return
    }

    // Create Navigator instance
    val navigator = remember(navController) { Navigator(navController) }

    NavHost(
        navController = navController,
        startDestination = resolvedDestination
    ) {
        // ==================== Authentication Routes ====================

        composable(Screen.Login.route) {
            LoginScreen(navigator)
        }

        composable(Screen.Registration.route) {
            RegistrationScreen(navigator)
        }

        // ==================== Main App Routes ====================
        
        composable(Screen.NotesList.route) {
            NotesListScreen(navigator)
        }

        composable(Screen.AddNote.route) {
            AddNotesScreen(navigator = navigator)
        }

        // ==================== Routes with Arguments ====================

        composable(
            route = Screen.UpdateNote.route,
            arguments = listOf(
                navArgument("noteId") { type = NavType.StringType },
                navArgument("isEditMode") { type = NavType.BoolType }
            )
        ) { backStackEntry ->
            val noteId = backStackEntry.savedStateHandle.get<String>("noteId") ?: ""
            val isEditMode = backStackEntry.savedStateHandle.get<Boolean>("isEditMode") ?: false
            UpdateNotesScreen(noteId = noteId, isEditMode = isEditMode, navigator = navigator)
        }

        composable(
            route = Screen.NoteDetail.route,
            arguments = listOf(
                navArgument("noteId") { type = NavType.StringType }
            )
        ) { backStackEntry ->
            val noteId = backStackEntry.savedStateHandle.get<String>("noteId") ?: ""
            // TODO: Add your NoteDetailScreen here
            // NoteDetailScreen(noteId = noteId, navigator = navigator)
        }

        composable(
            route = Screen.UpdateCategory.route,
            arguments = listOf(
                navArgument("categoryId") { type = NavType.StringType }
            )
        ) { backStackEntry ->
            val categoryId = backStackEntry.savedStateHandle.get<String>("categoryId") ?: ""
            // TODO: Add your UpdateCategoryScreen here
            // UpdateCategoryScreen(categoryId = categoryId, navigator = navigator)
        }


    }
}
